import moment from 'moment';
import ExcelJS from 'exceljs';
import React, { useState, useEffect } from 'react';
import { withAppContext } from 'context/AppContext';
import { Link, withRouter } from 'react-router-dom';
import { Button, Tag, Icon, Tooltip, Divider, Modal, message, Select, Spin, Input, Tabs, Table, Alert } from 'antd';
import { buildAccessTokenUri } from 'utils/routes';
import { constructColumn, constructColumnFilterRadio, handleOnAggregationTableChange } from 'utils/table/table';
import {
  getExtAuthenticationDetail,
  useGetExtAuthenticationActivity,
  exportExtAccessesCsv,
  deleteExtAuthentication,
  updateExtAuthenticationToken,
  updateExtAuthentication,
  getExtAccessHosts,
  addHostMappings,
  removeHostMapping,
  syncExtAccessForExtAuthentication,
  postPullNewPropertiesForExtAuthentication,
  getPullNewPropertyCandidates
} from 'utils/apis/accessToken';
import { ACCESS_TOKEN_ADMINS, API_SCOPE_OPTIONS, apiScopeLabel } from 'utils/constants';
import {
  StyledCard,
  HeaderSection,
  HeaderRow,
  HeaderMeta,
  HeaderLabel,
  TokenText,
  PageTitle,
  BackLink,
  HeaderActions,
  BodySection,
  SectionHeader,
  SectionTitle,
  EmptyState,
  HostCard,
  HostRow,
  HostRowLeft,
  HostRowRight,
  HostMeta,
  PropertyRow,
  PropertyRowLeft,
  PropertyName,
  PropertyMeta,
  MappingExpandedSection,
  MappingGroup,
  MappingGroupLabel,
  MappingTagList,
  NoPropertiesText
} from './AccessTokenDetail.styles';
import { PropertyAllowlistEditorBody, PropertyAllowlistEditorActions } from './PropertyAllowlistEditor';
import {
  normalizeExtAuthDetailForAllowlist,
  normalizePullCandidateRows,
  mergeSyncedDetailRowsWithPullCandidates,
  enrichPullRowsWithHostFromDetail,
  buildFallbackPropertyRowsFromDetail,
  getManageSyncPrimaryButtonState,
  buildPropertyAllowlistItems,
  MANAGE_SYNC_MSG
} from './pullPropertyAllowlistShared';

const { Option } = Select;

const isAccessTokenAdmin = user => user && user._id && ACCESS_TOKEN_ADMINS.includes(String(user._id));

const NEBULA_API_URL = 'https://nebulapi-asg.hostplatform.com';
const BORDER = { style: 'thin', color: { argb: 'FF000000' } };
const CELL_BORDER = { top: BORDER, left: BORDER, bottom: BORDER, right: BORDER };
const HEADER_FILL = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFD9D9D9' } };

const ACTIVITY_ACTION_OPTIONS = [
  { value: 'resync', label: 'Resync Token', color: 'cyan' },
  { value: 'updateToken', label: 'Change Token', color: 'blue' },
  { value: 'createToken', label: 'Create Token', color: 'purple' },
  { value: 'editName', label: 'Edit Token Name', color: 'gold' },
  { value: 'editApiScope', label: 'Edit API Scope', color: 'orange' },
  { value: 'addHost', label: 'Add Host', color: 'green' },
  { value: 'removeHost', label: 'Remove Host', color: 'red' }
];

const activityColumns = [
  {
    ...constructColumn('Date', 'createdAt', { hasSorter: true, width: 180 }),
    render: (val, record) => (val ? moment(val).format('YYYY-MM-DD hh:mm A') : '—')
  },
  {
    ...constructColumn('User', 'createdBy', { key: 'user', width: 140 }),
    dataIndex: ['createdBy', 'name'],
    render: (_, record) => {
      const u = record.createdBy;
      const name = u && (u.name || '').trim();
      return name || (u && u.username) || '—';
    }
  },
  {
    ...constructColumn('Action', 'action', { width: 200 }),
    ...constructColumnFilterRadio('action', ACTIVITY_ACTION_OPTIONS),
    onFilter: () => true,
    render: (val, record) => {
      const option = ACTIVITY_ACTION_OPTIONS.find(o => o.value === val);
      if (!option) return val || '—';
      const names = record.hostNames;
      const showHosts = (val === 'addHost' || val === 'removeHost') && Array.isArray(names) && names.length > 0;
      return (
        <div>
          <Tooltip title={option.label}>
            <Tag color={option.color}>{option.label}</Tag>
          </Tooltip>
          {showHosts && (
            <div style={{ marginTop: 6, fontSize: 12, color: '#666' }}>
              {names.map(name => (
                <div key={name}>– {name}</div>
              ))}
            </div>
          )}
        </div>
      );
    }
  }
];

const AccessTokenDetail = props => {
  const { user } = props;
  const id = props.match && props.match.params && props.match.params.id;
  const [detail, setDetail] = useState(null);
  const [pageLoading, setPageLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [copied, setCopied] = useState(false);
  const [addVisible, setAddVisible] = useState(false);
  const [selectedHosts, setSelectedHosts] = useState([]);
  const [modalLoading, setModalLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [exportCsvLoading, setExportCsvLoading] = useState(false);
  const [expandedHostIds, setExpandedHostIds] = useState({});
  const [expandedPropertyIds, setExpandedPropertyIds] = useState({});
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameInputValue, setNameInputValue] = useState('');
  const [nameSaveLoading, setNameSaveLoading] = useState(false);
  const [isEditingScopes, setIsEditingScopes] = useState(false);
  const [scopesInputValue, setScopesInputValue] = useState([]);
  const [scopesSaveLoading, setScopesSaveLoading] = useState(false);
  const [hostList, setHostList] = useState([]);
  const [activeTab, setActiveTab] = useState('mappings');
  const [activityQuery, setActivityQuery] = useState({ currentPage: 1, limit: 10, sorter: { createdAt: -1 } });
  const [partialResyncLoading, setPartialResyncLoading] = useState(false);
  const [pullNewOpen, setPullNewOpen] = useState(false);
  const [pullNewSubmitting, setPullNewSubmitting] = useState(false);
  const [pullNewRows, setPullNewRows] = useState([]);
  const [pullNewSelectedIds, setPullNewSelectedIds] = useState([]);
  const [pullNewUsedFallback, setPullNewUsedFallback] = useState(false);

  const { isLoading: activityLoading, paginatedData: activityList, total: activityTotal } = useGetExtAuthenticationActivity(
    id,
    {
      currentPage: activityQuery.currentPage,
      limit: activityQuery.limit,
      sorter: activityQuery.sorter || { createdAt: -1 },
      ...(activityQuery.filter && activityQuery.filter.action && { action: activityQuery.filter.action })
    },
    { shouldFetch: activeTab === 'activity' && !!id }
  );

  const fetchDetail = () => {
    if (!id) {
      setNotFound(true);
      setPageLoading(false);
      return Promise.resolve();
    }
    setPageLoading(true);
    return getExtAuthenticationDetail(id)
      .then(data => {
        const normalised = normalizeExtAuthDetailForAllowlist(data);
        if (normalised) {
          setDetail(normalised);
        } else {
          setNotFound(true);
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setPageLoading(false));
  };

  useEffect(() => {
    fetchDetail();
  }, [id]);

  if (!isAccessTokenAdmin(user)) {
    return (
      <StyledCard style={{ textAlign: 'center', padding: '48px 24px' }}>
        <p style={{ marginBottom: 16, color: '#666' }}>You don&apos;t have access to this page.</p>
        <Link to={buildAccessTokenUri()}>Back to Access Tokens</Link>
      </StyledCard>
    );
  }

  if (pageLoading) {
    return (
      <StyledCard>
        <div style={{ textAlign: 'center', padding: '60px 0' }}>
          <Spin size="large" />
        </div>
      </StyledCard>
    );
  }

  if (notFound || !detail) {
    return (
      <StyledCard>
        <div>
          Token not found. <Link to={buildAccessTokenUri()}>Back to Access Tokens</Link>
        </div>
      </StyledCard>
    );
  }

  const handleCopy = () => {
    navigator.clipboard.writeText(detail.token).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  const handleStartEditName = () => {
    setNameInputValue(detail.name || '');
    setIsEditingName(true);
  };

  const handleCancelEditName = () => {
    setIsEditingName(false);
    setNameInputValue('');
  };

  const handleSaveName = async () => {
    const trimmed = (nameInputValue || '').trim();
    if (!trimmed) {
      return message.warning('Name cannot be empty');
    }
    setNameSaveLoading(true);
    try {
      await updateExtAuthentication(id, { name: trimmed });
      message.success('Token name updated');
      setIsEditingName(false);
      setNameInputValue('');
      await fetchDetail();
    } catch (err) {
      message.error('Failed to update name');
    } finally {
      setNameSaveLoading(false);
    }
  };

  const handleStartEditScopes = () => {
    setScopesInputValue(Array.isArray(detail.apiScopes) ? [...detail.apiScopes] : []);
    setIsEditingScopes(true);
  };

  const handleCancelEditScopes = () => {
    setIsEditingScopes(false);
    setScopesInputValue([]);
  };

  const handleSaveScopes = async () => {
    if (!scopesInputValue.length) {
      return message.warning('Please select at least one API scope');
    }
    setScopesSaveLoading(true);
    try {
      await updateExtAuthentication(id, { apiScopes: scopesInputValue });
      message.success('API scope updated');
      setIsEditingScopes(false);
      setScopesInputValue([]);
      await fetchDetail();
    } catch (err) {
      message.error('Failed to update API scope');
    } finally {
      setScopesSaveLoading(false);
    }
  };

  const handleRevoke = () => {
    Modal.confirm({
      title: 'Revoke Token?',
      content: 'This token will no longer be able to authenticate. All host mappings will be deleted.',
      okText: 'Revoke',
      okType: 'danger',
      onOk: async () => {
        try {
          await deleteExtAuthentication(id);
          message.success('Token revoked successfully');
          props.history.push(buildAccessTokenUri());
        } catch (err) {
          message.error('Failed to revoke token');
        }
      }
    });
  };

  const handleUpdateToken = () => {
    Modal.confirm({
      title: 'Change Token?',
      content: 'A new token will be generated immediately. The current token will stop working. All host mappings remain unchanged.',
      okText: 'Confirm',
      onOk: async () => {
        try {
          await updateExtAuthenticationToken(id);
          message.success('Token changed successfully');
          await fetchDetail();
        } catch (err) {
          message.error('Failed to change token');
        }
      }
    });
  };

  const handleOpenAddHostModal = () => {
    setAddVisible(true);
    setModalLoading(true);

    // Only allow adding child hosts of already-mapped parent hosts
    const existingHostIdList = (detail.extAccesses || []).map(a => a.hostId).filter(Boolean);
    const existingHostIds = new Set(existingHostIdList);

    getExtAccessHosts(existingHostIdList, true) // true = get child hosts only
      .then(hosts => {
        const candidates = [];
        const list = Array.isArray(hosts) ? hosts : hosts && hosts.data ? hosts.data : [];
        list.forEach(parent => {
          const parentId = parent && parent._id ? String(parent._id) : null;
          if (!parentId) {
            return;
          }

          // Only consider children of parents that are already mapped
          if (!existingHostIds.has(parentId)) {
            return;
          }

          (parent.children || []).forEach(child => {
            const childId = child && child._id ? String(child._id) : null;
            if (!childId) {
              return;
            }

            // Skip hosts (parent or child) that are already mapped
            if (existingHostIds.has(childId)) {
              return;
            }

            candidates.push({
              value: childId,
              label: child.name || childId
            });
          });
        });

        setHostList(candidates);
      })
      .catch(() => message.error('Failed to load hosts'))
      .finally(() => setModalLoading(false));
  };

  const handleAddHost = async () => {
    if (!selectedHosts || selectedHosts.length === 0) {
      return message.warning('Please select at least one host');
    }
    setModalLoading(true);
    try {
      await addHostMappings(id, selectedHosts);
      message.success('Host mapping(s) added successfully');
      setAddVisible(false);
      setSelectedHosts([]);
      await fetchDetail();
    } catch (err) {
      message.error('Failed to add host mapping(s)');
    } finally {
      setModalLoading(false);
    }
  };

  const handleRemoveHost = extAccessId => {
    Modal.confirm({
      title: 'Remove Host Mapping?',
      content: 'This host and all its property mappings will be removed from this token.',
      okText: 'Remove',
      okType: 'danger',
      onOk: async () => {
        setActionLoading(extAccessId);
        try {
          await removeHostMapping(String(id), extAccessId);
          message.success('Host mapping removed');
          await fetchDetail();
        } catch (err) {
          message.error('Failed to remove host mapping');
        } finally {
          setActionLoading(null);
        }
      }
    });
  };

  const fetchPullNewCandidatesAndMerge = async () => {
    if (!id || !detail) {
      return;
    }
    let usedFallback = false;
    let apiRows = [];
    try {
      const data = await getPullNewPropertyCandidates(String(id));
      apiRows = normalizePullCandidateRows(data);
    } catch (e) {
      usedFallback = true;
    }
    const syncedRows = buildFallbackPropertyRowsFromDetail(detail);
    const rows = enrichPullRowsWithHostFromDetail(mergeSyncedDetailRowsWithPullCandidates(syncedRows, apiRows), detail);
    setPullNewRows(rows);
    setPullNewSelectedIds(prev => {
      const rowIdSet = new Set(rows.map(r => String(r.id)));
      const prevFiltered = prev.filter(pid => rowIdSet.has(String(pid)));
      const next = new Set(prevFiltered.map(String));
      rows.filter(r => r.alreadyOnToken).forEach(r => next.add(String(r.id)));
      return Array.from(next);
    });
    if (usedFallback) {
      setPullNewUsedFallback(true);
      message.warning(
        'Full PMS property list was not available. Showing properties already linked to this token. Implement GET …/candidates to list every host property before sync.'
      );
    } else {
      setPullNewUsedFallback(false);
    }
  };

  const openPullNewPropertiesModal = () => {
    if (!detail || !detail.extAccesses || !detail.extAccesses.length) {
      message.info('No host mappings to pull into');
      return;
    }
    const syncedRows = enrichPullRowsWithHostFromDetail(buildFallbackPropertyRowsFromDetail(detail), detail);
    setPullNewOpen(true);
    setPullNewRows(syncedRows);
    setPullNewSelectedIds(syncedRows.map(r => r.id));
    setPullNewUsedFallback(false);
    fetchPullNewCandidatesAndMerge();
  };

  const closePullNewPropertiesModal = () => {
    if (pullNewSubmitting) return;
    setPullNewOpen(false);
    setPullNewRows([]);
    setPullNewSelectedIds([]);
    setPullNewUsedFallback(false);
  };

  const manageSyncPrimary = getManageSyncPrimaryButtonState(pullNewRows, pullNewSelectedIds);

  const handlePullNewPropertiesSync = async () => {
    if (!id || !pullNewSelectedIds.length) {
      return message.error(MANAGE_SYNC_MSG.selectAtLeastOne);
    }
    if (manageSyncPrimary.hostMissingSelection) {
      return message.error(MANAGE_SYNC_MSG.eachHostNeedsOne);
    }
    setPullNewSubmitting(true);
    try {
      const idSet = new Set(pullNewSelectedIds.map(String));
      const selected = pullNewRows.filter(r => idSet.has(String(r.id)));
      const items = buildPropertyAllowlistItems(selected);
      if (items.length === 0 || items.length < selected.length) {
        return message.error(MANAGE_SYNC_MSG.allowlistRowNotResolvable);
      }
      await postPullNewPropertiesForExtAuthentication(String(id), {
        propertyAllowlist: { scope: 'selected', items }
      });
      message.success('Sync started');
      setPullNewOpen(false);
      setPullNewRows([]);
      setPullNewSelectedIds([]);
      setPullNewUsedFallback(false);
      await fetchDetail();
    } catch (err) {
      message.error('Failed to sync');
    } finally {
      setPullNewSubmitting(false);
    }
  };

  const handleResyncAllowlist = () => {
    if (!detail.extAccesses || !detail.extAccesses.length) {
      message.info('No host mappings to resync');
      return;
    }
    if (!id) {
      return;
    }
    Modal.confirm({
      title: 'Resync listed properties only?',
      content:
        'Re-sync only for properties already on this token. New properties would not be pulled in. To change which properties are allowed, use "Manage sync".',
      okText: 'Resync',
      maskClosable: true,
      onOk: async () => {
        setPartialResyncLoading(true);
        try {
          const extAccessIds = detail.extAccesses.map(a => a._id).filter(Boolean);
          await syncExtAccessForExtAuthentication(String(id), extAccessIds);
          message.success('Resync successful');
          await fetchDetail();
        } catch (err) {
          message.error('Failed to resync');
        } finally {
          setPartialResyncLoading(false);
        }
      }
    });
  };

  const buildExportWorkbookFromResults = (results, tokenDetail) => {
    const wb = new ExcelJS.Workbook();
    const styleLiveDocCell = cell => {
      cell.font = { bold: true };
      cell.border = CELL_BORDER;
    };
    const styleHeaderCell = cell => {
      cell.font = { bold: true };
      cell.fill = HEADER_FILL;
      cell.border = CELL_BORDER;
    };

    // Sheet 1: Live_Docs — once per export
    const wsLive = wb.addWorksheet('Live_Docs');
    wsLive.columns = [{ width: 18 }, { width: 45 }];
    [['Access-Token', (tokenDetail && tokenDetail.token) || ''], ['URL', NEBULA_API_URL], ['Environment', 'Production']].forEach(([label, value]) => {
      const row = wsLive.addRow([label, value]);
      styleLiveDocCell(row.getCell(1));
      styleLiveDocCell(row.getCell(2));
    });

    // Sheet 2: Hotel — one header, then each mapping's propertyList, blank row between blocks
    const wsHotel = wb.addWorksheet('Hotel');
    wsHotel.columns = [{ width: 20 }, { width: 40 }];
    wsHotel.addRow(['External ID', 'Property Name']).eachCell(styleHeaderCell);
    results.forEach((res, idx) => {
      (res.propertyList || []).forEach(p => wsHotel.addRow([p.propertyId, p.propertyName]));
      if (idx < results.length - 1) {
        wsHotel.addRow([]);
      }
    });

    // Sheet 3: RoomType — one header, then each mapping's roomList, blank row between blocks
    const wsRoom = wb.addWorksheet('RoomType');
    wsRoom.columns = [{ width: 40 }, { width: 20 }, { width: 40 }];
    wsRoom.addRow(['Property Name', 'External ID', 'Room Type']).eachCell(styleHeaderCell);
    results.forEach((res, idx) => {
      (res.roomList || []).forEach(r => wsRoom.addRow([r.propertyName, r.externalId, r.roomTypeName || r.roomType]));
      if (idx < results.length - 1) {
        wsRoom.addRow([]);
      }
    });

    // Sheet 4: Unit — one header, then each mapping's unitList, blank row between blocks
    const wsUnit = wb.addWorksheet('Unit');
    wsUnit.columns = [{ width: 30 }, { width: 15 }, { width: 35 }, { width: 40 }, { width: 20 }];
    wsUnit.addRow(['Unit Name', 'Unit Status', 'Property Name', 'Room Type', 'External ID']).eachCell(styleHeaderCell);
    results.forEach((res, idx) => {
      (res.unitList || []).forEach(u =>
        wsUnit.addRow([u.unitName, u.unitStatus || 'Active', u.propertyName, u.roomTypeName || u.roomType, u.unitId])
      );
      if (idx < results.length - 1) {
        wsUnit.addRow([]);
      }
    });

    return wb;
  };

  const downloadWorkbook = async (wb, filename) => {
    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', filename);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleExportCsv = async () => {
    if (!detail || !detail.extAccesses || !detail.extAccesses.length) {
      message.info('No host mappings to export');
      return;
    }

    const ids = detail.extAccesses.map(a => a._id);
    setExportCsvLoading(true);

    try {
      const data = await exportExtAccessesCsv(id, ids);
      if (!data || !data.length) {
        return message.error('Failed to export');
      }

      const wb = buildExportWorkbookFromResults(data, detail);
      const displayName = nameInputValue && nameInputValue.trim() ? nameInputValue.trim() : detail.name || 'Token';
      const filename = `ExtAPI_${displayName.replace(/\s+/g, '_')}.xlsx`;
      await downloadWorkbook(wb, filename);
      message.success('Export completed');
    } catch (err) {
      message.error('Failed to export');
    } finally {
      setExportCsvLoading(false);
    }
  };

  const toggleHost = hostId => {
    setExpandedHostIds(prev => ({ ...prev, [hostId]: !prev[hostId] }));
  };

  const toggleProperty = propId => {
    setExpandedPropertyIds(prev => ({ ...prev, [propId]: !prev[propId] }));
  };

  return (
    <StyledCard bodyStyle={{ padding: 0 }}>
      <HeaderSection>
        <HeaderRow>
          <div>
            <BackLink>
              <Link to={buildAccessTokenUri()}>
                <Icon type="left" style={{ marginRight: 8 }} />
                Back to Access Tokens
              </Link>
            </BackLink>
            {isEditingName ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                <Input
                  value={nameInputValue}
                  onChange={e => setNameInputValue(e.target.value)}
                  onPressEnter={handleSaveName}
                  style={{ width: 280 }}
                  placeholder="Token name"
                  autoFocus
                />
                <Button type="primary" size="small" loading={nameSaveLoading} onClick={handleSaveName}>
                  Save
                </Button>
                <Button size="small" onClick={handleCancelEditName} disabled={nameSaveLoading}>
                  Cancel
                </Button>
              </div>
            ) : (
              <PageTitle>
                {detail.name || '—'}
                <Tooltip title="Edit name">
                  <Button type="link" size="small" icon="edit" style={{ marginLeft: 8 }} onClick={handleStartEditName} />
                </Tooltip>
              </PageTitle>
            )}
            <HeaderMeta>
              <HeaderLabel>Token:</HeaderLabel>
              <TokenText>{detail.token ? detail.token.slice(0, 4) + '*'.repeat(Math.max(0, detail.token.length - 4)) : '—'}</TokenText>
              <Tooltip title={copied ? 'Copied!' : 'Copy token'}>
                <Button size="small" icon={copied ? 'check' : 'copy'} type={copied ? 'primary' : 'default'} onClick={handleCopy} />
              </Tooltip>
            </HeaderMeta>
            <HeaderMeta>
              <HeaderLabel>API scope:</HeaderLabel>
              {isEditingScopes ? (
                <>
                  <Select
                    mode="multiple"
                    size="small"
                    placeholder="Select at least one"
                    style={{ minWidth: 260 }}
                    value={scopesInputValue}
                    onChange={setScopesInputValue}
                  >
                    {API_SCOPE_OPTIONS.map(option => (
                      <Option key={option.value} value={option.value}>
                        {option.label}
                      </Option>
                    ))}
                  </Select>
                  <Button type="primary" size="small" loading={scopesSaveLoading} onClick={handleSaveScopes}>
                    Save
                  </Button>
                  <Button size="small" onClick={handleCancelEditScopes} disabled={scopesSaveLoading}>
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  {(detail.apiScopes || []).length
                    ? (detail.apiScopes || []).map(scope => (
                        <Tag key={scope} color={scope === 'messaging' ? 'purple' : 'blue'}>
                          {apiScopeLabel(scope)}
                        </Tag>
                      ))
                    : '—'}
                  <Tooltip title="Edit API scope">
                    <Button type="link" size="small" icon="edit" onClick={handleStartEditScopes} />
                  </Tooltip>
                </>
              )}
            </HeaderMeta>
            <HeaderMeta>
              <HeaderLabel>Created At:</HeaderLabel>
              {moment(detail.createdAt).format('YYYY-MM-DD hh:mm A')}
            </HeaderMeta>
          </div>

          <HeaderActions>
            <Button
              type="primary"
              icon="download"
              onClick={handleExportCsv}
              loading={exportCsvLoading}
              disabled={!detail.extAccesses || !detail.extAccesses.length}
            >
              Export CSV
            </Button>
            <Button icon="stop" type="danger" onClick={handleRevoke}>
              Revoke
            </Button>
            <Tooltip title="Refresh a new token code">
              <Button icon="key" onClick={handleUpdateToken}>
                Change Token
              </Button>
            </Tooltip>
          </HeaderActions>
        </HeaderRow>

        <Divider style={{ margin: 0 }} />
      </HeaderSection>

      <Tabs activeKey={activeTab} onChange={setActiveTab} style={{ padding: '24px 24px 24px' }}>
        <Tabs.TabPane tab="Host Mappings" key="mappings" forceRender>
          <BodySection>
            <SectionHeader>
              <SectionTitle>Host Mappings</SectionTitle>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
                <Tooltip title="Sync data only for properties already on this token">
                  <Button
                    type="primary"
                    icon="reload"
                    onClick={handleResyncAllowlist}
                    loading={partialResyncLoading}
                    disabled={!detail.extAccesses || !detail.extAccesses.length || pullNewOpen || pullNewSubmitting}
                  >
                    Resync
                  </Button>
                </Tooltip>
                <Tooltip title="Add / remove properties from this token">
                  <Button
                    icon="edit"
                    onClick={openPullNewPropertiesModal}
                    disabled={!detail.extAccesses || !detail.extAccesses.length || partialResyncLoading || pullNewOpen}
                  >
                    Manage sync
                  </Button>
                </Tooltip>
                <Tooltip title="Add child host to this token">
                  <Button icon="plus" onClick={handleOpenAddHostModal}>
                    Add Host
                  </Button>
                </Tooltip>
              </div>
            </SectionHeader>

            {detail.extAccesses.length === 0 ? (
              <EmptyState>No host mappings. Click "Add Host" to assign one.</EmptyState>
            ) : (
              detail.extAccesses.map((access, index) => {
                const isHostExpanded = expandedHostIds[access._id] === true;
                const isRemoveLoading = actionLoading === access._id;
                const isMainHost = index === 0;
                const onlyOneExtAccess = detail.extAccesses.length <= 1;
                // const canRemove = !onlyOneExtAccess && !isMainHost;
                // const removeTooltip = onlyOneExtAccess || isMainHost ? 'Main host cannot be removed' : 'Remove host';

                return (
                  <HostCard key={access._id}>
                    <HostRow onClick={() => toggleHost(access._id)}>
                      <HostRowLeft>
                        <Icon type={isHostExpanded ? 'down' : 'right'} style={{ color: '#888', fontSize: 11 }} />
                        <Icon type="cloud-server" style={{ color: '#1890ff' }} />
                        <span style={{ fontWeight: 600 }}>{access.hostName}</span>
                        <Tag style={{ fontSize: 11, fontFamily: 'monospace' }}>{access.externalId}</Tag>
                      </HostRowLeft>
                      <HostRowRight>
                        <HostMeta>
                          {access.properties.length} {access.properties.length === 1 ? 'property' : 'properties'}
                        </HostMeta>
                        <HostMeta>Last sync: {access.updatedAt ? moment(access.updatedAt).format('YYYY-MM-DD hh:mm A') : '—'}</HostMeta>
                        <Tooltip>
                          {/* <Tooltip title={removeTooltip}> */}
                          <span>
                            <Button
                              type="link"
                              danger
                              size="small"
                              icon="delete"
                              loading={isRemoveLoading}
                              // disabled={!canRemove}
                              onClick={e => {
                                e.stopPropagation();
                                // if (canRemove) handleRemoveHost(access._id);
                                handleRemoveHost(access._id);
                              }}
                              // style={{ padding: '0 4px', height: 'auto', color: canRemove ? '#ff4d4f' : undefined }}
                              style={{ padding: '0 4px', height: 'auto', color: '#ff4d4f' }}
                            >
                              Remove
                            </Button>
                          </span>
                        </Tooltip>
                      </HostRowRight>
                    </HostRow>

                    {isHostExpanded &&
                      (access.properties.length === 0 ? (
                        <NoPropertiesText>No properties mapped.</NoPropertiesText>
                      ) : (
                        access.properties.map(prop => {
                          const isPropExpanded = expandedPropertyIds[prop._id] === true;

                          return (
                            <div key={prop._id}>
                              <PropertyRow onClick={() => toggleProperty(prop._id)}>
                                <PropertyRowLeft>
                                  <Icon type={isPropExpanded ? 'down' : 'right'} style={{ color: '#aaa', fontSize: 10 }} />
                                  <Icon type="bank" style={{ color: '#888' }} />
                                  <PropertyName>{prop.name}</PropertyName>
                                  <Tag style={{ fontSize: 11, fontFamily: 'monospace' }}>{prop.externalId}</Tag>
                                </PropertyRowLeft>
                                <PropertyMeta>
                                  {prop.rooms.length} room types · {prop.units.length} units
                                </PropertyMeta>
                              </PropertyRow>

                              {isPropExpanded && (
                                <MappingExpandedSection>
                                  {prop.rooms.length > 0 && (
                                    <MappingGroup>
                                      <MappingGroupLabel>Room Types</MappingGroupLabel>
                                      <MappingTagList>
                                        {prop.rooms.map(room => (
                                          <Tag key={room._id} style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                            {room.externalId}
                                            <span style={{ fontFamily: 'sans-serif', color: '#888', marginLeft: 6 }}>{room.name}</span>
                                          </Tag>
                                        ))}
                                      </MappingTagList>
                                    </MappingGroup>
                                  )}

                                  {prop.units.length > 0 && (
                                    <MappingGroup style={{ borderTop: prop.rooms.length > 0 ? '1px solid #f0f0f0' : 'none', paddingBottom: 12 }}>
                                      <MappingGroupLabel>Units</MappingGroupLabel>
                                      <MappingTagList>
                                        {prop.units.map(unit => (
                                          <Tag key={unit._id} color="blue" style={{ fontFamily: 'monospace', fontSize: 12 }}>
                                            {unit.externalId}
                                            <span style={{ fontFamily: 'sans-serif', opacity: 0.85, marginLeft: 6 }}>{unit.name}</span>
                                          </Tag>
                                        ))}
                                      </MappingTagList>
                                    </MappingGroup>
                                  )}
                                </MappingExpandedSection>
                              )}
                            </div>
                          );
                        })
                      ))}
                  </HostCard>
                );
              })
            )}
          </BodySection>
        </Tabs.TabPane>

        <Tabs.TabPane tab="Activity Log" key="activity">
          <div style={{ paddingTop: 16 }}>
            <Table
              rowKey={(record, idx) => record._id || idx}
              columns={activityColumns}
              dataSource={activityList}
              loading={activityLoading}
              onChange={(pagination, filters, sorter) => handleOnAggregationTableChange({ pagination, filters, sorter }, setActivityQuery)}
              scroll={{ x: true }}
              showSorterTooltip={false}
              pagination={{
                total: activityTotal,
                current: activityQuery.currentPage,
                pageSize: activityQuery.limit || 10,
                showLessItems: true
              }}
            />
          </div>
        </Tabs.TabPane>
      </Tabs>

      <Modal
        title="Manage sync"
        visible={pullNewOpen}
        onCancel={closePullNewPropertiesModal}
        footer={null}
        width={720}
        destroyOnClose
        maskClosable={!pullNewSubmitting}
        closable={!pullNewSubmitting}
      >
        <>
          {pullNewUsedFallback && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 12 }}
              message="Limited list"
              description="Could not load extra properties from PMS. Only properties already on this token are listed. Implement GET /extAuthentication/…/pull-new-properties/candidates for the full host property list."
            />
          )}
          <PropertyAllowlistEditorBody
            propertyRows={pullNewRows}
            selectedIds={pullNewSelectedIds}
            onSelectedIdsChange={setPullNewSelectedIds}
            tableScrollY={320}
            showSyncStatus
            groupByHost
            hostGroupsExtAccesses={detail && detail.extAccesses ? detail.extAccesses : []}
            enforceMinSelected
            enforcePerHostMinSelected
          />
          <div style={{ marginTop: 16 }}>
            <PropertyAllowlistEditorActions
              onClose={closePullNewPropertiesModal}
              onSaveAndSync={handlePullNewPropertiesSync}
              closeLabel="Cancel"
              primaryLabel="Sync"
              primaryLoading={pullNewSubmitting}
              primaryDisabled={manageSyncPrimary.disabled}
              primaryDisabledReason={manageSyncPrimary.disabledReason}
            />
          </div>
        </>
      </Modal>

      <Modal
        title="Add Child Hosts"
        visible={addVisible}
        onCancel={() => {
          setAddVisible(false);
          setSelectedHosts([]);
        }}
        onOk={handleAddHost}
        okText="Confirm"
        confirmLoading={modalLoading}
      >
        <div style={{ marginBottom: 8, fontWeight: 500 }}>Select Host(s)</div>
        <Select
          mode="multiple"
          showSearch
          placeholder={modalLoading ? 'Loading hosts...' : 'Select one or more hosts to assign'}
          style={{ width: '100%' }}
          value={selectedHosts}
          onChange={setSelectedHosts}
          optionFilterProp="children"
          loading={modalLoading}
          disabled={modalLoading}
          filterOption={(input, option) => option.props.children.toLowerCase().includes(input.toLowerCase())}
        >
          {hostList.map(h => (
            <Option key={h.value} value={h.value}>
              {h.label}
            </Option>
          ))}
        </Select>
        {!modalLoading && hostList.length === 0 && (
          <div style={{ marginTop: 8, color: '#aaa', fontSize: 12 }}>No hosts available to add. All hosts may already be mapped to this token.</div>
        )}
      </Modal>
    </StyledCard>
  );
};

export default withRouter(withAppContext(AccessTokenDetail));
